import { useMemo, useState } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { fmt, fmtOpt } from '../../utils/numberFormat';
import { puToVolts } from '../../utils/electricalConstants';
import { LOADING_COMPLIANCE_LIMIT_PCT } from '../../utils/loadingConstants';
import { ROLLOUT_DIRECTION_LABEL as DIRECTION_LABEL } from '../../utils/planningLabels';
import type { PlanningRolloutDirectionResult } from '../../types/planningRollout';

const KIND_LABEL: Record<string, string> = { trafostation: 'Trafo', line: 'Kabel', hausanschluss: 'HA' };

interface ChartPoint {
  year: string;
  index: number;
  /** Every Monte-Carlo run of the year collapsed — no percentiles, no frame. */
  collapsed: boolean;
  /** The base frame of the direction does not converge — the whole direction has no result. */
  noBase: boolean;
  p50: number;
  p95: number;
  p: number;
}
const pct = (v: number) => `${Math.round(v * 100)}`;

/**
 * Result of a rollout job: Stützjahr scrubber with the P50/P95 trajectory of
 * the max loading, KPIs of the selected year and direction, and the list of
 * elements with their first risk / expected-violation year.
 */
export function RolloutResults() {
  const rollout = useStressTestStore(s => s.rollout);
  const job = useStressTestStore(s => s.rolloutJob);
  const yearIndex = useStressTestStore(s => s.rolloutYearIndex);
  const direction = useStressTestStore(s => s.rolloutDirection);
  const selectRolloutYear = useStressTestStore(s => s.selectRolloutYear);
  const stressElements = useStressTestStore(s => s.stressElements);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const clearResults = useStressTestStore(s => s.clearResults);
  const [showAll, setShowAll] = useState(false);

  const chart = useMemo(() => {
    if (!rollout) return null;
    // A year whose runs all collapsed (no converged load flow) has no percentiles:
    // it is drawn as a collapse marker, not as "0 %".
    const point = (year: string, index: number, d: PlanningRolloutDirectionResult | undefined): ChartPoint => ({
      year,
      index,
      collapsed: d != null && (d.status === 'base_not_converged' || (d.nonConvergedRuns > 0 && !d.representativeFrame)),
      noBase: d?.status === 'base_not_converged',
      p50: d?.maxLoadingP50Pct ?? 0,
      p95: d?.maxLoadingP95Pct ?? 0,
      p: d?.anyViolationProbability ?? 0,
    });
    const points = [
      point('heute', -1, rollout.today.find(d => d.direction === direction)),
      ...rollout.years.map((y, i) => point(String(y.year), i, y.directions.find(x => x.direction === direction))),
    ];
    // Chart headroom: at least 10 % above the legal cap, 5 % above the highest P95.
    const maxY = Math.max(LOADING_COMPLIANCE_LIMIT_PCT * 1.1, ...points.filter(p => !p.collapsed).map(p => p.p95)) * 1.05;
    return { points, maxY, anyCollapsed: points.some(p => p.collapsed && !p.noBase), noBase: points.some(p => p.noBase) };
  }, [rollout, direction]);

  // A failed job replaces the progress card in place — the small error line of
  // the sidebar alone was easy to miss ("30 s Ladekreis, dann passiert nichts").
  if (job && job.state === 'failed') {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
        <p className="text-sm text-red-700 dark:text-red-300 font-medium mb-1">Hochlauf fehlgeschlagen</p>
        <p className="text-xs text-red-700 dark:text-red-400 leading-snug">{job.error ?? 'Unbekannter Fehler'}</p>
        {job.total > 0 && (
          <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">{job.done} / {job.total} Lastflüsse waren gerechnet.</p>
        )}
      </div>
    );
  }

  if (job && job.state !== 'done' && job.state !== 'failed') {
    const ratio = job.total > 0 ? job.done / job.total : 0;
    return (
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3">
        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1.5">Hochlauf wird gerechnet…</p>
        <div className="h-2 rounded-full bg-blue-100 dark:bg-blue-900/40 overflow-hidden">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.round(ratio * 100)}%` }} />
        </div>
        <p className="text-[11px] text-blue-700 dark:text-blue-300 mt-1">{job.done} / {job.total || '…'} Lastflüsse</p>
      </div>
    );
  }

  if (!rollout || !chart) return null;

  const selected = yearIndex < 0
    ? rollout.today.find(d => d.direction === direction)
    : rollout.years[yearIndex]?.directions.find(d => d.direction === direction);
  const yearResult = yearIndex >= 0 ? rollout.years[yearIndex] : null;
  const firstYear = direction === 'feedIn'
    ? rollout.firstViolationYearFeedIn
    : direction === 'summerPeak'
      ? rollout.firstViolationYearSummerPeak
      : rollout.firstViolationYearPeakLoad;
  const violations = rollout.firstViolations.filter(v => v.direction === direction);
  const visible = showAll ? violations : violations.slice(0, 8);
  const warnings = rollout.confidence.filter(c => c.severity === 'warning');

  const select = (elementId: string) => {
    const el = stressElements.find(e => e.id === elementId)
      ?? stressElements.find(e => 'segments' in e && (e as { segments?: { id: string }[] }).segments?.some(seg => seg.id === elementId));
    if (el) setSelectedElement(el);
  };

  // Simple SVG trajectory: P95 band + P50 line, legal-cap limit line.
  const W = 260;
  const H = 90;
  const padL = 26;
  const padB = 16;
  const padR = 16;
  const x = (i: number) => padL + (i * (W - padL - padR)) / Math.max(chart.points.length - 1, 1);
  const y = (v: number) => H - padB - (v / chart.maxY) * (H - padB - 6);
  // Collapsed years break the lines (no value), they never pull the curve to 0.
  const linePath = (value: (p: ChartPoint) => number) => {
    let path = '';
    let open = false;
    chart.points.forEach((p, i) => {
      if (p.collapsed) {
        open = false;
        return;
      }
      path += `${open ? 'L' : 'M'}${x(i).toFixed(1)},${y(value(p)).toFixed(1)} `;
      open = true;
    });
    return path.trim();
  };
  const p50Path = linePath(p => p.p50);
  const p95Path = linePath(p => p.p95);
  const selectedNoBase = selected?.status === 'base_not_converged';
  const selectedCollapsed = selected != null && (selectedNoBase || (selected.nonConvergedRuns > 0 && !selected.representativeFrame));

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100 flex items-center gap-1.5">
            Hochlauf
            {rollout.controlBoxActivation && (
              <span className="normal-case tracking-normal text-[10px] px-1 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200" title="Gerechnet mit §14a-Steuerung: steuerbare Verbrauchseinrichtungen im Starklastfall auf P_min gedimmt">§14a aktiv</span>
            )}
          </h3>
          <span className="text-[10px] text-gray-500 dark:text-primary-400">{rollout.monteCarloRuns} Läufe · {rollout.totalSolves} Lastflüsse · {fmt(rollout.durationSeconds, 0)} s</span>
        </div>

        {/* Direction */}
        <div className="flex rounded-lg border border-gray-300 dark:border-primary-500 overflow-hidden text-xs mb-2">
          <button type="button" onClick={() => selectRolloutYear(yearIndex, 'peakLoad')} className={`flex-1 px-2 py-1 font-medium ${direction === 'peakLoad' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200'}`}>Starklast</button>
          <button type="button" onClick={() => selectRolloutYear(yearIndex, 'feedIn')} className={`flex-1 px-2 py-1 font-medium ${direction === 'feedIn' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200'}`}>Einspeisung</button>
          <button type="button" title="Heißer Sommerabend: Wallbox + Klimaanlagen, WP aus, PV ≈ 0" onClick={() => selectRolloutYear(yearIndex, 'summerPeak')} className={`flex-1 px-2 py-1 font-medium ${direction === 'summerPeak' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200'}`}>Sommer</button>
        </div>

        {/* Trajectory */}
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24 text-gray-500 dark:text-primary-300" role="img" aria-label="Max. Auslastung je Stützjahr">
          <line x1={padL} x2={W - padR} y1={y(LOADING_COMPLIANCE_LIMIT_PCT)} y2={y(LOADING_COMPLIANCE_LIMIT_PCT)} stroke="#dc2626" strokeDasharray="3 3" strokeWidth="1" />
          <text x={padL - 2} y={y(LOADING_COMPLIANCE_LIMIT_PCT) + 3} fontSize="8" textAnchor="end" fill="#dc2626">{fmt(LOADING_COMPLIANCE_LIMIT_PCT, 0)}</text>
          <text x={padL - 2} y={y(0) + 3} fontSize="8" textAnchor="end" fill="currentColor">0</text>
          <path d={p95Path} fill="none" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="2 2" />
          <path d={p50Path} fill="none" stroke="#2563eb" strokeWidth="1.8" />
          {chart.points.map((p, i) => (
            <g key={p.year} onClick={() => selectRolloutYear(p.index)} className="cursor-pointer">
              {p.collapsed ? (
                <text x={x(i)} y={y(chart.maxY) + 8} fontSize="11" textAnchor="middle" fill="#dc2626" fontWeight={700}>
                  <title>{p.noBase ? 'Grundzustand nicht rechenbar — kein Ergebnis' : 'Alle Läufe nicht konvergiert (Spannungskollaps)'}</title>✕
                </text>
              ) : (
                <circle cx={x(i)} cy={y(p.p50)} r={p.index === yearIndex ? 4 : 2.5} fill={p.p50 > LOADING_COMPLIANCE_LIMIT_PCT ? '#dc2626' : '#2563eb'} stroke={p.index === yearIndex ? '#111827' : 'none'} strokeWidth="1" />
              )}
              <text x={x(i)} y={H - 4} fontSize="8" textAnchor="middle" fill="currentColor" fontWeight={p.index === yearIndex ? 700 : 400}>{p.year}</text>
            </g>
          ))}
        </svg>
        <p className="text-[10px] text-gray-500 dark:text-primary-400 -mt-1 mb-2">
          <span className="text-blue-600">— P50</span> · <span className="text-amber-500">‥ P95</span> der max. Auslastung je Stützjahr; Punkt anklicken = Jahr auf der Karte.
          {chart.anyCollapsed && <> <span className="text-red-600 font-bold">✕</span> = alle Läufe nicht konvergiert (Spannungskollaps), kein Lastfluss für die Karte.</>}
          {chart.noBase && <> <span className="text-red-600 font-bold">✕</span> = Grundzustand dieser Lastrichtung nicht rechenbar, kein Ergebnis.</>}
        </p>

        {/* Selected year */}
        {selected && (
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <div className={`rounded-lg p-2 text-center ${selectedCollapsed || (selected.maxLoadingP50Pct ?? 0) > LOADING_COMPLIANCE_LIMIT_PCT ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
              <p className="text-[10px] text-gray-600 dark:text-primary-400">{yearResult ? yearResult.year : 'Heute'} · Max. Auslastung</p>
              {selectedNoBase ? (
                <>
                  <p className="text-base font-bold text-red-700 dark:text-red-300">Nicht rechenbar</p>
                  <p className="text-[10px] text-red-700 dark:text-red-300">Grundzustand konvergiert ohne Zubau nicht</p>
                </>
              ) : selectedCollapsed ? (
                <>
                  <p className="text-base font-bold text-red-700 dark:text-red-300">Kollaps</p>
                  <p className="text-[10px] text-red-700 dark:text-red-300">{selected.nonConvergedRuns === 1 ? 'Lastfluss' : `alle ${selected.nonConvergedRuns} Läufe`} nicht konvergiert</p>
                </>
              ) : (
                <>
                  <p className="text-base font-bold text-gray-800 dark:text-primary-100 tabular-nums">{fmtOpt(selected.maxLoadingP50Pct, 0) ?? '–'} %</p>
                  <p className="text-[10px] text-gray-500 dark:text-primary-400">P95 {fmtOpt(selected.maxLoadingP95Pct, 0) ?? '–'} % · max {fmtOpt(selected.maxLoadingMaxPct, 0) ?? '–'} %</p>
                </>
              )}
            </div>
            <div className={`rounded-lg p-2 text-center ${selectedNoBase ? 'bg-gray-50 dark:bg-primary-800' : selected.anyViolationProbability >= 0.5 ? 'bg-red-50 dark:bg-red-900/20' : selected.anyViolationProbability >= 0.1 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
              <p className="text-[10px] text-gray-600 dark:text-primary-400">P(Grenzwertbruch)</p>
              <p className="text-base font-bold text-gray-800 dark:text-primary-100 tabular-nums">{selectedNoBase ? '–' : `${pct(selected.anyViolationProbability)} %`}</p>
              <p className="text-[10px] text-gray-500 dark:text-primary-400">{selectedNoBase ? 'kein Ergebnis' : <>{selected.elementsExpectedToViolate} erwartet · {selected.elementsAtRisk} gefährdet{selected.nonConvergedRuns > 0 ? ` · ${selected.nonConvergedRuns} Kollaps` : ''}</>}</p>
            </div>
            <div className="rounded-lg p-2 text-center bg-blue-50 dark:bg-blue-900/20">
              <p className="text-[10px] text-gray-600 dark:text-primary-400">{direction === 'feedIn' ? 'Max. Spannung P95' : 'Min. Spannung P50'}</p>
              <p className="text-base font-bold text-gray-800 dark:text-primary-100 tabular-nums">
                {selectedCollapsed
                  ? '—'
                  : direction === 'feedIn'
                    ? `${fmt(puToVolts(selected.maxVoltageP95Pu ?? 1), 0)} V`
                    : `${fmt(puToVolts(selected.minVoltageP50Pu ?? 1), 0)} V`}
              </p>
              <p className="text-[10px] text-gray-500 dark:text-primary-400">
                {selectedCollapsed ? 'kein Arbeitspunkt' : direction === 'feedIn' ? '' : `P05 ${fmt(puToVolts(selected.minVoltageP05Pu ?? 1), 0)} V`}
              </p>
            </div>
            <div className="rounded-lg p-2 text-center bg-purple-50 dark:bg-purple-900/20">
              <p className="text-[10px] text-purple-600 dark:text-purple-400">Durchdringung</p>
              <p className="text-[11px] font-semibold text-purple-700 dark:text-purple-300 tabular-nums leading-snug">
                {yearResult
                  ? `PV ${pct(yearResult.pvShare)} · WP ${pct(yearResult.heatPumpShare)} · WB ${pct(yearResult.wallboxShare)} · Sp ${pct(yearResult.storageShare)} · Kl ${pct(yearResult.acShare)} %`
                  : `PV ${pct(rollout.bestand.pvShare)} · WP ${pct(rollout.bestand.heatPumpShare)} · WB ${pct(rollout.bestand.wallboxShare)} · Sp ${pct(rollout.bestand.storageShare)} · Kl ${pct(rollout.bestand.acShare)} %`}
              </p>
              {yearResult && <p className="text-[10px] text-gray-500 dark:text-primary-400">+{fmt(yearResult.addedLoadKwMean, 0)} kW Last, +{fmt(yearResult.addedPvKwpMean, 0)} kWp</p>}
            </div>
          </div>
        )}

        <p className={`text-[11px] mb-2 ${firstYear ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
          {firstYear
            ? `Erster erwarteter Grenzwertbruch (${DIRECTION_LABEL[direction]}): ${firstYear}`
            : `Kein erwarteter Grenzwertbruch bis ${rollout.years[rollout.years.length - 1]?.year} (${DIRECTION_LABEL[direction]})`}
        </p>

        {/* First violations per element */}
        {violations.length > 0 && (
          <div className="mb-2">
            <h4 className="text-[10px] uppercase tracking-wider text-gray-700 dark:text-primary-200 font-semibold mb-1">Elemente nach erstem Risikojahr</h4>
            <ul className="divide-y divide-gray-100 dark:divide-primary-600">
              {visible.map(v => (
                <li key={v.elementId}>
                  <button type="button" onClick={() => select(v.elementId)} className="w-full text-left py-1 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-primary-600 rounded px-1">
                    <span className={`shrink-0 text-[10px] px-1 rounded ${v.firstViolationYear ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>{KIND_LABEL[v.kind] ?? v.kind}</span>
                    <span className="flex-1 min-w-0 truncate text-xs text-gray-800 dark:text-primary-100" title={v.name ?? v.elementId}>{v.name ?? v.elementId}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-gray-600 dark:text-primary-300">
                      {v.firstRiskYear ? `Risiko ${v.firstRiskYear}` : ''}{v.firstViolationYear ? ` · Bruch ${v.firstViolationYear}` : ''}
                    </span>
                    <span className="shrink-0 w-10 text-right text-[11px] font-semibold tabular-nums text-gray-700 dark:text-primary-200">{pct(v.finalViolationProbability)} %</span>
                  </button>
                </li>
              ))}
            </ul>
            {violations.length > 8 && (
              <button type="button" onClick={() => setShowAll(v => !v)} className="mt-1 text-[11px] text-accent-primary hover:underline">
                {showAll ? 'Weniger anzeigen' : `Alle ${violations.length} anzeigen`}
              </button>
            )}
          </div>
        )}

        {warnings.length > 0 && (
          <ul className="mb-2 space-y-1">
            {warnings.map(c => (
              <li key={c.factor} className="text-[11px] leading-snug text-amber-700 dark:text-amber-300 flex gap-1.5"><span className="shrink-0">▲</span><span>{c.reason}</span></li>
            ))}
          </ul>
        )}

        <button
          onClick={clearResults}
          className="w-full px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300 dark:bg-primary-700 dark:hover:bg-primary-600 dark:text-primary-100 dark:border-primary-500 rounded-lg text-xs font-medium"
        >
          Zurücksetzen
        </button>
      </div>
    </div>
  );
}
